import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getKnownLoginDevices } from '@api/settings';
import { useAppStore } from '@stores/app';
import { KnownDevice } from 'types/settings';

export const useKnownDevicesStore = defineStore('knownDevices', () => {
	const devices = ref<Array<KnownDevice>>([]);
	const currentDeviceId = ref<string>('');
	const nextPageToken = ref<string | undefined>(undefined);

	const devicesLength = computed((): number => devices.value.length);

	const getDeviceById = computed(() => (deviceId: string): KnownDevice | undefined => {
		return devices.value.find((device) => device.deviceId === deviceId);
	});

	const hasNextPage = computed((): boolean => !!nextPageToken.value);

	async function storeDevices(pageSize = 5): Promise<void> {
		if (devicesLength.value === 0) {
			const response = await getKnownLoginDevices(pageSize);
			devices.value = response.page.results;
			currentDeviceId.value = response.currentDeviceId;
			nextPageToken.value = response.page.nextPageToken;
		}
	}

	async function fetchNextPage(pageSize = 5): Promise<void> {
		const response = await getKnownLoginDevices(pageSize, nextPageToken.value);
		devices.value = [...devices.value, ...response.page.results];
		currentDeviceId.value = response.currentDeviceId;
		nextPageToken.value = response.page.nextPageToken;
	}

	function clearDevice(deviceId: string): void {
		devices.value = devices.value.filter((device) => device.deviceId !== deviceId);
		if (currentDeviceId.value === deviceId) {
			currentDeviceId.value = '';
		}
	}

	function clearAllDevices(): void {
		devices.value = [];
		currentDeviceId.value = '';
		nextPageToken.value = undefined;
	}

	function $reset(): void {
		clearAllDevices();
	}

	useAppStore().$subscribe(
		(_, state) => {
			if (state.tokenScope === 'UNAUTHENTICATED') {
				$reset();
			}
		},
		{ detached: true }
	);

	return {
		devices,
		currentDeviceId,
		nextPageToken,
		devicesLength,
		getDeviceById,
		hasNextPage,
		storeDevices,
		fetchNextPage,
		clearDevice,
		clearAllDevices,
		$reset
	};
});
